import React, { useEffect, useState } from "react";
import Input from "../../components/Input";
import { useForm } from "react-hook-form";
import { useLoginMutation } from "../../apis/loginApis";
import toast from "react-hot-toast";
import OtpForm from "../Otp/OtpForm";
import { addProfileData } from "../../features/auth/authSlice";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { useProfileData } from "../../apis/profileApis";
import LanguagePicker from "../../components/common/LanguagePicker";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(
    localStorage.getItem("rememberMe") === "true"
  );
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const { mutate, isPending } = useLoginMutation();
  const { refetch } = useProfileData();

  const handleLogin = (data: any) => {
    setEmail(data?.email);
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", data.email);
      localStorage.setItem("rememberedPassword", data.password);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("rememberedPassword");
      localStorage.setItem("rememberMe", "false");
    }
    mutate(
      { ...data, type: "LM" },
      {
        onSuccess: async (responseData) => {
          console.log("Login Success:", responseData);
          if (!responseData?.otpCode) {
            const profileResponse = await refetch();
            if (profileResponse?.data) {
              dispatch(addProfileData(profileResponse?.data));
              const user = profileResponse?.data;
              const hasNoWarehouse = !user?.isWarehouseCreated && !user?.warehouses?.length;
              
              if (hasNoWarehouse && user?.clientType !== 'DS') {
                navigate("/create-warehouse");
              } else {
                navigate("/dashboard");
              }
            } else {
              toast.error("Failed to load user profile.");
            }
          } else {
            // If OTP is required, show the OtpForm
            setIsSuccess(true);
          }
        },
        onError: (error: any) => {
          console.error("Login Error:", error);
          toast.error(`Login failed: ${error}`);
        },
      }
    );
  };

  useEffect(() => {
    const remember = localStorage.getItem("rememberMe");
    if (remember === "true") {
      setValue("email", localStorage.getItem("rememberedEmail"));
      setValue("password", localStorage.getItem("rememberedPassword"));
    }
  }, [setValue]);

  return (
    <div>
      <div className="flex items-center justify-center h-screen p-4 bg-gradient-to-l from-[#FFE783] to-[#E4E5E7]">
        <div className=" w-[40%] relative h-full flex justify-center items-center ">
          {!isSuccess ? (
            <div className="flex flex-col items-center gap-6">
              <div className="space-y-8 p-6 2xl:w-[28rem] xl:w-[26rem] w-8/12">
                <div className="flex justify-start">
                  <img src="/img/logo-tracktech-black.svg" alt="Logo" className="2xl:w-60 w-52" />
                </div>

                <div className="text-left">
                  <h2 className="2xl:text-3xl text-2xl font-medium ">Welcome Back</h2>
                  <p className="text-[#000000]/70 2xl:text-base text-sm">
                    Welcome Back! Please Enter Your Details.
                  </p>
                </div>

                {/* REMOVED method="POST" action="#" */}
                <form onSubmit={handleSubmit(handleLogin)}>
                  <div className="space-y-4">
                    <Input
                      name="email"
                      type="text"
                      title="Email"
                      placeholder="Enter Your Email"
                      inputClassName="rounded-lg w-full py-2.5 px-4 text-base text-[#1E1E1E] border border-[#D8D8D8]"
                      register={register}
                      error={errors.email}
                      validation={{
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Please enter a valid email address",
                        },
                      }}
                    />

                    <Input
                      name="password"
                      type="password"
                      title="Password"
                      placeholder="Enter Your Password"
                      inputClassName="rounded-lg w-full py-2.5 px-4 text-base text-[#1E1E1E] border border-[#D8D8D8]"
                      register={register}
                      error={errors.password}
                      validation={{ required: "Password is required" }}
                    />

                    <div className="flex justify-between items-center">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          className="accent-[#FBD037] !scale-110"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <span className="text-[#1E1E1E] text-sm">Remember</span>
                      </label>

                      <NavLink to="/forgot-password" className="text-[#007BFF] text-sm">
                        Forgot password?
                      </NavLink>
                    </div>
                  </div>

                  <div className="pt-6">
                    <button
                      type="submit"
                      disabled={isPending}
                      className={`w-full flex justify-center items-center gap-2 font-semibold py-3 rounded-lg transition ${
                        isPending
                          ? "bg-[#212121] cursor-not-allowed text-[#ffffff]"
                          : "bg-[#FBD037] hover:bg-yellow-400 text-[#000000]"
                      }`}
                    >
                      {isPending ? "Logging in..." : "Login"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <OtpForm email={email} />
          )}
        </div>
        {/* Illustration Section */}
        <div className="w-[60%] h-full flex justify-center relative bg-cover overflow-hidden">
          <img src="./img/dashboard-icons/login-illustraion.svg" className="w-10/12 mt-6" alt="login-screen" />
        </div>
      </div>
    </div>
  );
};

export default Login;
